import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  BarChart3, Users, Eye, Clock, TrendingUp, 
  Globe, Smartphone, Monitor, RefreshCw, 
  Zap, Shield, Accessibility, Search,
  ExternalLink, AlertTriangle, CheckCircle
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AnalyticsDashboard = () => {
  const [lighthouseData, setLighthouseData] = useState(null);
  const [loadingLighthouse, setLoadingLighthouse] = useState(false);
  const [selectedPage, setSelectedPage] = useState('https://its-sold.com');

  const pagesToTest = [
    { url: 'https://its-sold.com', label: 'Homepage' },
    { url: 'https://its-sold.com/seller-resources', label: 'Seller Resources' },
    { url: 'https://its-sold.com/buyer-resources', label: 'Buyer Resources' },
    { url: 'https://its-sold.com/homes-for-sale/dallas', label: 'Dallas Landing' },
    { url: 'https://its-sold.com/homes-for-sale/plano', label: 'Plano Landing' },
    { url: 'https://its-sold.com/blog', label: 'Blog' },
  ];

  const fetchLighthouseData = async (url) => {
    setLoadingLighthouse(true);
    try {
      // Using Google PageSpeed Insights API (free, no key required for basic usage)
      const apiUrl = `https://www.googleapis.com/pagespeedonline/v5/runPagespeed?url=${encodeURIComponent(url)}&category=performance&category=accessibility&category=best-practices&category=seo`;
      
      const response = await fetch(apiUrl);
      const data = await response.json();
      
      if (data.lighthouseResult) {
        const categories = data.lighthouseResult.categories;
        setLighthouseData({
          url: url,
          performance: Math.round(categories.performance?.score * 100) || 0,
          accessibility: Math.round(categories.accessibility?.score * 100) || 0,
          bestPractices: Math.round(categories['best-practices']?.score * 100) || 0,
          seo: Math.round(categories.seo?.score * 100) || 0,
          fetchTime: new Date().toLocaleTimeString(),
          audits: data.lighthouseResult.audits
        });
        toast.success('Lighthouse analysis complete!');
      } else {
        toast.error('Failed to get Lighthouse data');
      }
    } catch (error) {
      console.error('Lighthouse error:', error);
      toast.error('Failed to fetch Lighthouse data');
    } finally {
      setLoadingLighthouse(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return 'text-green-600 bg-green-100';
    if (score >= 50) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getScoreIcon = (score) => {
    if (score >= 90) return <CheckCircle className="w-5 h-5 text-green-600" />;
    if (score >= 50) return <AlertTriangle className="w-5 h-5 text-yellow-600" />;
    return <AlertTriangle className="w-5 h-5 text-red-600" />;
  };

  return (
    <div className="space-y-6">
      {/* GA4 Quick Links */}
      <Card className="p-6 bg-gradient-to-r from-blue-600 to-blue-700 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="w-7 h-7" />
              Google Analytics 4
            </h2>
            <p className="text-blue-100 mt-1">Property ID: G-2PCWM08Q0L</p>
          </div>
          <div className="flex gap-3">
            <a 
              href="https://analytics.google.com/analytics/web/#/p/G-2PCWM08Q0L/reports/reportinghub" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button className="bg-white text-blue-700 hover:bg-blue-50">
                <Eye className="w-4 h-4 mr-2" />
                View Reports
              </Button>
            </a>
            <a 
              href="https://analytics.google.com/analytics/web/#/p/G-2PCWM08Q0L/reports/dashboard" 
              target="_blank" 
              rel="noopener noreferrer"
            >
              <Button variant="outline" className="border-white text-white hover:bg-blue-600">
                <TrendingUp className="w-4 h-4 mr-2" />
                Real-time
              </Button>
            </a>
          </div>
        </div>
        
        {/* Quick Stats Placeholder */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <Users className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-sm opacity-80">Active Users</p>
            <p className="text-2xl font-bold">--</p>
            <p className="text-xs opacity-60">View in GA4</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <Eye className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-sm opacity-80">Page Views</p>
            <p className="text-2xl font-bold">--</p>
            <p className="text-xs opacity-60">View in GA4</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <Clock className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-sm opacity-80">Avg. Session</p>
            <p className="text-2xl font-bold">--</p>
            <p className="text-xs opacity-60">View in GA4</p>
          </div>
          <div className="bg-white/10 rounded-lg p-4 text-center">
            <TrendingUp className="w-8 h-8 mx-auto mb-2 opacity-80" />
            <p className="text-sm opacity-80">Bounce Rate</p>
            <p className="text-2xl font-bold">--</p>
            <p className="text-xs opacity-60">View in GA4</p>
          </div>
        </div>
      </Card>

      {/* Lighthouse / PageSpeed Insights */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Zap className="w-6 h-6 text-orange-500" />
              PageSpeed Insights (Lighthouse)
            </h2>
            <p className="text-gray-500 text-sm">Analyze page performance, SEO, and accessibility</p>
          </div>
        </div>

        {/* Page Selector */}
        <div className="flex flex-wrap gap-2 mb-6">
          {pagesToTest.map((page) => (
            <Button
              key={page.url}
              variant={selectedPage === page.url ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedPage(page.url)}
              className={selectedPage === page.url ? 'bg-orange-500 hover:bg-orange-600' : ''}
            >
              {page.label}
            </Button>
          ))}
        </div>

        <div className="flex items-center gap-4 mb-6">
          <input
            type="text"
            value={selectedPage}
            onChange={(e) => setSelectedPage(e.target.value)}
            className="flex-1 px-4 py-2 border rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            placeholder="Enter URL to test..."
          />
          <Button 
            onClick={() => fetchLighthouseData(selectedPage)}
            disabled={loadingLighthouse}
            className="bg-orange-500 hover:bg-orange-600"
          >
            {loadingLighthouse ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Analyzing...
              </>
            ) : (
              <>
                <Zap className="w-4 h-4 mr-2" />
                Run Analysis
              </>
            )}
          </Button>
        </div>

        {/* Results */}
        {lighthouseData && (
          <div className="space-y-6">
            <div className="flex items-center justify-between text-sm text-gray-500">
              <span>Results for: {lighthouseData.url}</span>
              <span>Last updated: {lighthouseData.fetchTime}</span>
            </div>

            {/* Score Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className={`rounded-xl p-6 text-center ${getScoreColor(lighthouseData.performance)}`}>
                <Zap className="w-10 h-10 mx-auto mb-2" />
                <p className="text-4xl font-bold">{lighthouseData.performance}</p>
                <p className="text-sm font-medium mt-1">Performance</p>
              </div>
              <div className={`rounded-xl p-6 text-center ${getScoreColor(lighthouseData.accessibility)}`}>
                <Accessibility className="w-10 h-10 mx-auto mb-2" />
                <p className="text-4xl font-bold">{lighthouseData.accessibility}</p>
                <p className="text-sm font-medium mt-1">Accessibility</p>
              </div>
              <div className={`rounded-xl p-6 text-center ${getScoreColor(lighthouseData.bestPractices)}`}>
                <Shield className="w-10 h-10 mx-auto mb-2" />
                <p className="text-4xl font-bold">{lighthouseData.bestPractices}</p>
                <p className="text-sm font-medium mt-1">Best Practices</p>
              </div>
              <div className={`rounded-xl p-6 text-center ${getScoreColor(lighthouseData.seo)}`}>
                <Search className="w-10 h-10 mx-auto mb-2" />
                <p className="text-4xl font-bold">{lighthouseData.seo}</p>
                <p className="text-sm font-medium mt-1">SEO</p>
              </div>
            </div>

            {/* Key Metrics */}
            {lighthouseData.audits && (
              <div className="grid md:grid-cols-2 gap-4">
                <Card className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Clock className="w-4 h-4 text-blue-500" />
                    Core Web Vitals
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span>First Contentful Paint (FCP)</span>
                      <span className="font-mono">{lighthouseData.audits['first-contentful-paint']?.displayValue || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Largest Contentful Paint (LCP)</span>
                      <span className="font-mono">{lighthouseData.audits['largest-contentful-paint']?.displayValue || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Total Blocking Time (TBT)</span>
                      <span className="font-mono">{lighthouseData.audits['total-blocking-time']?.displayValue || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Cumulative Layout Shift (CLS)</span>
                      <span className="font-mono">{lighthouseData.audits['cumulative-layout-shift']?.displayValue || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Speed Index</span>
                      <span className="font-mono">{lighthouseData.audits['speed-index']?.displayValue || 'N/A'}</span>
                    </div>
                  </div>
                </Card>

                <Card className="p-4">
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <Search className="w-4 h-4 text-green-500" />
                    SEO Checks
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span>Meta Description</span>
                      {getScoreIcon(lighthouseData.audits['meta-description']?.score * 100)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Document Title</span>
                      {getScoreIcon(lighthouseData.audits['document-title']?.score * 100)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Crawlable Links</span>
                      {getScoreIcon(lighthouseData.audits['crawlable-anchors']?.score * 100)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Mobile Friendly</span>
                      {getScoreIcon(lighthouseData.audits['viewport']?.score * 100)}
                    </div>
                    <div className="flex justify-between items-center">
                      <span>Valid Robots.txt</span>
                      {getScoreIcon(lighthouseData.audits['robots-txt']?.score * 100)}
                    </div>
                  </div>
                </Card>
              </div>
            )}

            {/* External Link */}
            <div className="text-center">
              <a 
                href={`https://pagespeed.web.dev/report?url=${encodeURIComponent(lighthouseData.url)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center text-blue-600 hover:text-blue-800"
              >
                View Full Report on PageSpeed Insights
                <ExternalLink className="w-4 h-4 ml-1" />
              </a>
            </div>
          </div>
        )}

        {!lighthouseData && !loadingLighthouse && (
          <div className="text-center py-12 text-gray-500">
            <Zap className="w-16 h-16 mx-auto mb-4 opacity-30" />
            <p>Select a page and click "Run Analysis" to see performance metrics</p>
          </div>
        )}
      </Card>

      {/* Google Search Console Quick Link */}
      <Card className="p-6 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold flex items-center gap-2">
              <Globe className="w-6 h-6" />
              Google Search Console
            </h2>
            <p className="text-green-100 mt-1">Monitor indexing status and search performance</p>
          </div>
          <a 
            href="https://search.google.com/search-console?resource_id=https%3A%2F%2Fits-sold.com%2F" 
            target="_blank" 
            rel="noopener noreferrer"
          >
            <Button className="bg-white text-green-700 hover:bg-green-50">
              <ExternalLink className="w-4 h-4 mr-2" />
              Open Search Console
            </Button>
          </a>
        </div>
      </Card>
    </div>
  );
};

export default AnalyticsDashboard;
